import { type NoteResult, type PartLine, type SuggestedGrade, suggestGrade, type TakeScoring } from '@corocanto/scoring';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LocalTake } from '../takes/store';
import { api, ApiError } from './client';
import type { VoicePart } from './types';

export type Grade = SuggestedGrade;
export type GradeSource = 'DIRECTOR' | 'SECTION_LEADER' | 'ACCEPTED_FROM_APP';

export interface GradeInfo {
  takeId: string;
  value: Grade;
  source: GradeSource;
  suggestedValue: Grade | null;
  /** Null when the account was deleted. */
  setByName: string | null;
  createdAt: string;
}

export interface TakeComment {
  id: string;
  songId: string;
  takeId: string | null;
  /** Null when the account was deleted. */
  authorName: string | null;
  authorRole: 'DIRECTOR' | 'SECTION_LEADER' | 'CHORIST';
  /** The section the author leads as Grup Lideri, or null. */
  authorLeadsSection: VoicePart | null;
  /** Written measure index and number of the bar it is about, when there is one. */
  measureIndex: number | null;
  measureNumber: string | null;
  body: string;
  createdAt: string;
}

/** A submitted take as stored on the server (scores as computed in the chorist's browser). */
export interface SubmittedTake {
  id: string;
  choirId: string;
  songId: string;
  songTitle: string;
  userId: string;
  choristName: string;
  part: VoicePart;
  /** The line sung in a divided part (1 upper, 2 lower), or null. */
  line: PartLine | null;
  fromIndex: number;
  toIndex: number;
  fromNumber: string;
  toNumber: string;
  tempoFactor: number;
  referencePitchHz: number | null;
  latencyMs: number | null;
  durationMs: number | null;
  audioTimelineStartSec: number | null;
  audioContentType: string;
  overallScore: number | null;
  pitchScore: number | null;
  rhythmScore: number | null;
  suggestedGrade: Grade;
  scoredBy: 'BROWSER' | 'SERVER';
  scoringVersion: string | null;
  scoring: TakeScoring;
  notes: NoteResult[];
  submittedAt: string;
  grade: GradeInfo | null;
  previousGrade: GradeInfo | null;
  comments: TakeComment[];
}

/** A submission waiting for the director's grade. */
export interface ReviewItem {
  takeId: string;
  userId: string;
  choristName: string;
  /** The section the chorist leads as Grup Lideri in this choir, or null. */
  leadsSection: VoicePart | null;
  part: VoicePart;
  line: PartLine | null;
  songId: string;
  songTitle: string;
  fromNumber: string;
  toNumber: string;
  overallScore: number | null;
  suggestedGrade: Grade;
  submittedAt: string;
}

/** Where the signed-in chorist stands on one song. */
export interface SongProgress {
  songId: string;
  lastTakeId: string;
  lastSubmittedAt: string;
  lastFromNumber: string;
  lastToNumber: string;
  awaitingGrade: boolean;
  grade: GradeInfo | null;
  comments: TakeComment[];
}

const takeKey = (choirId: string, takeId: string) => ['take', choirId, takeId] as const;
const reviewKey = (choirId: string) => ['review', choirId] as const;
const progressKey = (choirId: string) => ['progress', choirId] as const;

/** Uploads a practice take kept in this browser: its recording and the browser's results. */
export function useSubmitTake(choirId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (take: LocalTake) => {
      if (!take.audio) throw new Error('The take has no recording');
      const form = new FormData();
      form.append('audio', take.audio, 'take');
      form.append(
        'take',
        JSON.stringify({
          localTakeId: take.id,
          part: take.part,
          line: take.line ?? null,
          fromIndex: take.fromIndex,
          toIndex: take.toIndex,
          fromNumber: take.fromNumber,
          toNumber: take.toNumber,
          tempoFactor: take.tempoFactor ?? take.tempoPercent / 100,
          referencePitchHz: take.referenceA4,
          latencyMs: Math.round(take.latencyMs),
          durationMs: take.durationMs ?? null,
          partial: take.partial,
          audioTimelineStartSec: take.audioTimelineStartSec,
          scoringVersion: take.scoringVersion,
          suggestedGrade: suggestGrade(take.scoring.scores.overall),
          scoring: take.scoring,
          notes: take.notes,
        }),
      );
      return api<SubmittedTake>(`/api/choirs/${choirId}/songs/${take.songId}/takes`, { method: 'POST', form });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: progressKey(choirId) });
    },
  });
}

export function useSubmittedTake(choirId: string | undefined, takeId: string | undefined) {
  return useQuery({
    queryKey: takeKey(choirId ?? '', takeId ?? ''),
    queryFn: () => api<SubmittedTake>(`/api/choirs/${choirId}/takes/${takeId}`),
    enabled: !!choirId && !!takeId,
  });
}

/** The recording of a submitted take. */
export function useTakeAudio(choirId: string | undefined, takeId: string | undefined) {
  return useQuery({
    queryKey: ['takeAudio', choirId, takeId],
    queryFn: async () => {
      const response = await fetch(`/api/choirs/${choirId}/takes/${takeId}/audio`, { credentials: 'same-origin' });
      if (!response.ok) throw new ApiError(response.status, undefined, response.statusText);
      return response.blob();
    },
    enabled: !!choirId && !!takeId,
    staleTime: Infinity,
  });
}

export function useReviewQueue(choirId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: reviewKey(choirId ?? ''),
    queryFn: () => api<ReviewItem[]>(`/api/choirs/${choirId}/review`),
    enabled: !!choirId && enabled,
    refetchInterval: 60_000,
  });
}

export function useGradeTake(choirId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ takeId, value, acceptSuggestion }: { takeId: string; value: Grade; acceptSuggestion: boolean }) =>
      api<SubmittedTake>(`/api/choirs/${choirId}/takes/${takeId}/grade`, { method: 'POST', json: { value, acceptSuggestion } }),
    onSuccess: (take) => {
      queryClient.setQueryData(takeKey(choirId, take.id), take);
      void queryClient.invalidateQueries({ queryKey: reviewKey(choirId) });
    },
  });
}

export function useAcceptSuggestions(choirId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (takeIds: string[]) =>
      api<{ accepted: number }>(`/api/choirs/${choirId}/review/accept`, { method: 'POST', json: { takeIds } }),
    onSuccess: (_, takeIds) => {
      void queryClient.invalidateQueries({ queryKey: reviewKey(choirId) });
      for (const id of takeIds) void queryClient.invalidateQueries({ queryKey: takeKey(choirId, id) });
    },
  });
}

export function useAddComment(choirId: string, takeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (comment: { body: string; measureIndex: number | null; measureNumber: string | null }) =>
      api<TakeComment>(`/api/choirs/${choirId}/takes/${takeId}/comments`, { method: 'POST', json: comment }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: takeKey(choirId, takeId) }),
  });
}

/** The signed-in chorist's submissions, grades and comments per song. */
export function useMyProgress(choirId: string | undefined) {
  return useQuery({
    queryKey: progressKey(choirId ?? ''),
    queryFn: () => api<SongProgress[]>(`/api/choirs/${choirId}/me/progress`),
    enabled: !!choirId,
  });
}
